import { createTheme } from "@mui/material";

import { tokens } from "./tokens";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    background: {
      default: tokens.navy[900],
      paper: tokens.navy[800],
    },
    primary: {
      main: tokens.accent.main,
      dark: tokens.accent.hover,
      light: tokens.accent.soft,
      contrastText: tokens.text.onAccent,
    },
    secondary: {
      main: tokens.navy[600],
      light: tokens.navy[700],
      dark: tokens.navy[950],
      contrastText: tokens.text.primary,
    },
    success: { main: tokens.semantic.success, contrastText: "#fff" },
    error: { main: tokens.semantic.error, contrastText: "#fff" },
    info: { main: tokens.semantic.info },
    warning: { main: tokens.accent.main },
    text: {
      primary: tokens.text.primary,
      secondary: tokens.text.secondary,
      disabled: tokens.text.disabled,
    },
    divider: tokens.border,
    common: { black: tokens.navy[950], white: "#fff" },
  },
  spacing: 8,
  shape: { borderRadius: 10 },
  typography: {
    fontFamily:
      'Inter, "Noto Sans", system-ui, -apple-system, sans-serif',
    h4: {
      fontSize: "1.75rem",
      fontWeight: 600,
      letterSpacing: "-0.02em",
      lineHeight: 1.25,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
      letterSpacing: "-0.01em",
      lineHeight: 1.3,
    },
    h6: {
      fontSize: "0.95rem",
      fontWeight: 600,
      lineHeight: 1.4,
      textTransform: "none",
      letterSpacing: "0.04em",
    },
    subtitle1: {
      fontSize: "1rem",
      lineHeight: 1.5,
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: "0.875rem",
      lineHeight: 1.45,
      fontWeight: 500,
    },
    button: {
      fontWeight: 600,
      letterSpacing: 0,
    },
    caption: {
      fontSize: "0.75rem",
      lineHeight: 1.4,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "html, body, #root": {
          height: "100%",
        },
        body: {
          backgroundColor: tokens.navy[900],
          color: tokens.text.primary,
        },
        "*::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "*::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "*::-webkit-scrollbar-thumb": {
          background: tokens.navy[600],
          borderRadius: "8px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          border: `1px solid ${tokens.border}`,
        },
        elevation1: {
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
        },
        elevation2: {
          boxShadow: "0 12px 32px rgba(0, 0, 0, 0.28)",
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: "none",
          fontWeight: 600,
          padding: "8px 18px",
        },
        containedPrimary: {
          color: tokens.text.onAccent,
          "&:hover": {
            backgroundColor: tokens.accent.hover,
          },
        },
        contained: {
          "&.Mui-disabled": {
            color: tokens.text.disabled,
            backgroundColor: tokens.navy[700],
          },
        },
        outlined: {
          borderColor: tokens.border,
        },
        text: {
          color: tokens.text.secondary,
          "&:hover": {
            backgroundColor: tokens.accent.muted,
            color: tokens.text.primary,
          },
        },
      },
    },
    MuiIconButton: {
      defaultProps: { disableRipple: true },
      styleOverrides: {
        root: {
          color: tokens.text.secondary,
          borderRadius: 10,
          "&:hover": {
            color: tokens.text.primary,
            backgroundColor: tokens.navy[700],
          },
          "&:focus": { outline: "none" },
          "&:focus-visible": {
            outline: `2px solid ${tokens.accent.main}`,
            outlineOffset: 2,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& input:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0 30px ${tokens.navy[700]} inset !important`,
            WebkitTextFillColor: `${tokens.text.primary} !important`,
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.navy[700],
          borderRadius: 10,
          border: `1px solid ${tokens.border}`,
          "&:hover": {
            backgroundColor: tokens.navy[700],
          },
          "&.Mui-focused": {
            backgroundColor: tokens.navy[700],
            borderColor: tokens.accent.main,
          },
          "&.Mui-disabled": {
            backgroundColor: tokens.navy[900],
          },
          "&::before, &::after": {
            display: "none",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.navy[700],
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.navy[600],
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: tokens.accent.main,
          },
        },
        notchedOutline: {
          borderColor: tokens.border,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: tokens.text.secondary,
          "&.Mui-focused": {
            color: tokens.accent.main,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          backgroundColor: "transparent",
        },
        icon: {
          color: tokens.text.secondary,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: "2px 6px",
          "&.Mui-selected": {
            backgroundColor: `${tokens.accent.muted} !important`,
            color: tokens.accent.soft,
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.navy[800],
          border: `1px solid ${tokens.border}`,
          boxShadow: "none",
          "&:before": { display: "none" },
          "&.Mui-expanded": { margin: "8px 0" },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.navy[900],
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.navy[700],
          borderRadius: 10,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          color: tokens.text.secondary,
          textTransform: "none",
          borderColor: tokens.border,
          "&.Mui-selected": {
            backgroundColor: tokens.accent.muted,
            color: tokens.accent.soft,
            "&:hover": {
              backgroundColor: tokens.accent.muted,
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          "&:focus": { outline: "none" },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: tokens.accent.main,
          height: 3,
          borderRadius: 3,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.navy[800],
          backgroundImage: "none",
          border: `1px solid ${tokens.border}`,
        },
      },
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.navy[700],
          fontSize: 13,
          border: `1px solid ${tokens.border}`,
        },
        arrow: { color: tokens.navy[700] },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(10, 12, 18, 0.56)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: tokens.navy[950],
          borderRight: `1px solid ${tokens.border}`,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.navy[700],
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: tokens.text.secondary,
          borderColor: tokens.border,
        },
        body: {
          borderColor: tokens.border,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: tokens.border,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.navy[800],
        },
        listbox: {
          backgroundColor: tokens.navy[800],
        },
      },
    },
  },
});

export default darkTheme;
